import { useMemo, useState, useEffect, useRef, memo } from 'react'
import { useTranslation } from 'react-i18next'
import debugWrapper from '../utils/debugWrapper'
import { 
  UserIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  BellIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'
import GlassmorphButton from './ui/GlassmorphButton'

/**
 * ImmersiveTabBar - Reusable tab navigation for immersive pages
 * 
 * Features:
 * - Icon-based tab buttons with active states
 * - Conditional tab visibility (e.g., hide signup when authenticated)
 * - Notification badges support
 * - Customizable prepend elements (e.g., back button)
 * - Memoized for performance
 * 
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab ID
 * @param {string} props.panelState - Current panel state ('minimized', 'semi', 'expanded')
 * @param {Function} props.onTabClick - Callback when tab is clicked (tabId) => void
 * @param {boolean} props.isAuthenticated - Whether user is authenticated
 * @param {Object} props.badges - Object mapping tab IDs to badge counts { contact: 5 }
 * @param {React.ReactNode} props.prependElement - Optional element to render before tabs (e.g., back button)
 * @param {Array} props.customTabs - Optional custom tabs configuration (overrides default)
 * 
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: render tab bar
 */
const ImmersiveTabBar = memo(function ImmersiveTabBar({
  activeTab,
  panelState,
  onTabClick,
  isAuthenticated = false,
  badges = {},
  prependElement = null,
  customTabs = null,
  extraTabs = [],
  isRail = false
}) {
  // Use translation hook directly to subscribe to language changes
  const { t, i18n } = useTranslation()

  // Container-aware sizing: observe the parent element's actual rendered width so
  // button size adapts to available space rather than relying on viewport breakpoints.
  const tabBarRef = useRef(null)
  const [availableWidth, setAvailableWidth] = useState(null)
  useEffect(() => {
    const parent = tabBarRef.current?.parentElement
    if (!parent) return
    setAvailableWidth(parent.getBoundingClientRect().width || null)
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setAvailableWidth(entry.contentRect.width)
    })
    ro.observe(parent)
    return () => ro.disconnect()
  }, []) // parent element is stable after mount

  // Default tab configuration
  const defaultTabs = [
    { id: 'search', icon: MagnifyingGlassIcon, label: t('nav.search', 'Search') },
    { id: 'announcements', icon: BellIcon, label: t('nav.announcements', 'News') },
    { id: 'faq', icon: QuestionMarkCircleIcon, label: t('nav.faq', 'FAQ') },
    { id: 'auth', icon: UserIcon, label: t('nav.account', 'Account') },
    { id: 'contact', icon: ChatBubbleLeftRightIcon, label: t('nav.contact', 'Contact') },
    ...extraTabs
  ]

  const tabs = customTabs || defaultTabs

  // Filter tabs based on authentication status (memoized to prevent recalculation)
  const visibleTabs = useMemo(() => 
    tabs.filter(tab => !tab.hideWhenAuthenticated || !isAuthenticated),
    [tabs, isAuthenticated]
  )

  // Include optional prepend element (e.g. back button) in per-slot calculation
  const itemCount = visibleTabs.length + (prependElement ? 1 : 0)

  // Derive button size from pixels-per-slot in the actual container.
  // Thresholds match actual GlassmorphButton render widths at each size:
  //   md=44px (p-2.5 + w-5 + border-2), mobile=36px (p-2 + w-4 + border-2), sm=32px (p-1.5 + w-4 + border-2), xs=26px
  // Using justify-evenly means buttons share the container width equally — no fixed gap accumulation.
  const tabSize = useMemo(() => {
    if (!availableWidth) {
      // Pre-measurement fallback: approximate from viewport width
      return typeof window !== 'undefined' && window.innerWidth >= 640 ? 'md' : 'mobile'
    }
    const perSlot = availableWidth / itemCount
    if (perSlot >= 44) return 'md'
    if (perSlot >= 36) return 'mobile'
    if (perSlot >= 32) return 'sm'
    return 'xs'
  }, [availableWidth, itemCount])

  // Memoize the tabs JSX to prevent recreation on every render
  const tabsElement = useMemo(() => {
    // Rail mode: vertical icon column inside the 64px collapsed panel
    if (isRail) {
      return (
        <div className="flex flex-col items-center gap-1 w-full">
          {/* Prepend element (e.g., back button) visible even when rail is collapsed */}
          {prependElement && (
            <div className="w-full flex justify-center mb-1">
              {prependElement}
            </div>
          )}
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.id
            const badgeCount = badges[tab.id] || 0
            return (
              <div key={tab.id} className="relative w-full flex justify-center">
                <GlassmorphButton
                  icon={tab.icon}
                  label={tab.label}
                  onClick={() => onTabClick(tab.id)}
                  isActive={isActive}
                  badge={null}
                  variant="icon"
                  size="sm"
                />
                {badgeCount > 0 && (
                  <span className="absolute top-0.5 right-1.5 w-2 h-2 bg-red-500 rounded-full pointer-events-none" />
                )}
              </div>
            )
          })}
        </div>
      )
    }

    // Normal horizontal mode
    return (
      <div ref={tabBarRef} className="flex items-center w-full justify-evenly">
        {/* Optional prepend element (e.g., back button) */}
        {prependElement}
        
        {/* Tab buttons */}
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.id && panelState !== 'minimized'
          const badgeCount = badges[tab.id] || 0
          
          return (
            <GlassmorphButton
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              onClick={() => onTabClick(tab.id)}
              isActive={isActive}
              badge={badgeCount > 0 ? badgeCount : null}
              variant="icon"
              size={tabSize}
            />
          )
        })}
      </div>
    )
  }, [isRail, visibleTabs, activeTab, panelState, badges, prependElement, onTabClick, tabSize])

  return tabsElement
})

export default debugWrapper(ImmersiveTabBar, 'ImmersiveTabBar', 'src/components/ImmersiveTabBar.jsx')
